import { format } from 'date-fns';
import { parseISO } from 'date-fns';
import type { Locale } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import isLocale from 'date-fns/locale/is';
import { getCurrentLangPretty } from '../url/urlHelpers';

type DateInput = Date | string | number | null | undefined;

const localeMap: Record<'en' | 'is', Locale> = {
	en: enUS,
	is: isLocale,
};

const ensureDate = (value: DateInput): Date | null => {
	if (value === null || value === undefined) {
		return null;
	}

	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}

	if (typeof value === 'number') {
		const date = new Date(value);
		return Number.isNaN(date.getTime()) ? null : date;
	}

	if (typeof value === 'string') {
		if (value.trim().length === 0) {
			return null;
		}

		try {
			const parsed = parseISO(value);
			if (!Number.isNaN(parsed.getTime())) {
				return parsed;
			}
		} catch {
			// Swallow parse errors and attempt a fallback parse below.
		}

		const fallback = new Date(value);
		return Number.isNaN(fallback.getTime()) ? null : fallback;
	}

	return null;
};

const getDefaultLocale = () => {
	return localeMap[getCurrentLangPretty()];
};

interface FormatOptions {
	locale?: Locale;
}

export const formatDate = (value: DateInput, pattern: string, options: FormatOptions = {}): string => {
	const date = ensureDate(value);
	if (!date) {
		return '';
	}

	return format(date, pattern, {
		locale: options.locale ?? getDefaultLocale(),
	});
};

export const toDate = (value: DateInput): Date | null => {
	return ensureDate(value);
};
